import type {
  LayoutListActionNode,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import { useMemo } from "react";

export interface ActionLayoutListNodeProps {
  node: LayoutListActionNode;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  rowVariantsNames: string[];
  onCreateRowVariantNameBlur?: (nodeId: string, value: string) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
}

type PortRow = { input?: Port; output?: Port };

function buildPortRows(node: LayoutListActionNode): PortRow[] {
  const rows: PortRow[] = [];
  rows.push(...node.ports.map((port) => ({ input: port })));
  return rows;
}

/**
 * Blueprint nodes for declarative animation chains (`animBegin`, `animKeyframe`, `animEnd`).
 * Input and output ports are laid out on the same row (left / right), including flow handles.
 */
export const ActionLayoutListNode = ({
  node,
  onCreateRowVariantNameBlur,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
}: ActionLayoutListNodeProps) => {
  const layoutList = dico().behavior.layoutList.createRowVariant;
  const title = layoutList.label;
  const headerBg =
    "linear-gradient(180deg, #93c5fd 0%, #2563eb 55%, #1e3a8a 100%)";

  const rows = useMemo(() => buildPortRows(node), [node]);

  const renderPort = (port: Port) => {
    return (
      <PortHandle
        node={node}
        port={port}
        setPortsMap={setPortsMap}
        onMouseDown={() => onPortMouseDown?.(node.id, port)}
        onMouseUp={() => onPortMouseUp?.(node.id, port)}
        showValueInput={true}
        onValueChange={(value) => onPortValueChange?.(node.id, port.id, value)}
        onValueBlur={(value) => {
          if (node.kind === "createRowVariant" && port.id === "name") {
            onCreateRowVariantNameBlur?.(node.id, String(value ?? ""));
          }
        }}
        isConnected={isPortConnected?.(node.id, port.id) ?? false}
      />
    );
  };

  return (
    <div
      style={{
        minWidth: 250,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background: headerBg,
          textAlign: "center",
        }}
      >
        {title}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rows.map((row, idx) => {
          const paired = Boolean(row.input && row.output);

          return (
            <div
              key={`row-${idx}-${row.input?.id ?? ""}-${row.output?.id ?? ""}`}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                gap: 10,
                minHeight: 32,
              }}
            >
              <div
                style={{
                  flex: paired ? 1 : undefined,
                  width: paired ? undefined : "100%",
                  minWidth: 0,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                {row.input ? renderPort(row.input) : null}
              </div>
              {paired ? (
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {row.output ? renderPort(row.output) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
